import {
  Anchor,
  Button,
  Divider,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { getCloudMetadata, getCurrentUser } from "./getCurrentUser";
import { ApiError, RestApi } from "./RestApi";

export function Login({ returnTo: returnToProp }: { returnTo?: string } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const user = getCurrentUser();
  const metadata = getCloudMetadata();
  const returnTo = returnToProp ?? params.get("return") ?? "/";
  const normalizedReturn = returnTo.startsWith("/") ? returnTo : "/" + returnTo;

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (user) {
      navigate(normalizedReturn, { replace: true });
    }
  }, [user, normalizedReturn, navigate]);

  const onGoogleSignIn = () => {
    // Put return path in Google OAuth state (server /signin/google) so desktop /applogin
    // auto-mint still works without relying on sessionStorage across the OAuth round-trip.
    window.location.href =
      "/signin/google?return=" + encodeURIComponent(normalizedReturn);
  };

  const onEmailSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await RestApi.localAuthLogin(email.trim(), password);
      window.location.href = normalizedReturn;
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("signin.emailPasswordError");
      setError(message);
      setSubmitting(false);
    }
  };

  if (user) {
    return (
      <Stack gap="md" maw={480}>
        <Title order={3}>{t("signin.alreadySignedIn")}</Title>
        <Text>{t("signin.signedInAs", { email: user.AccountEmail || user.Email })}</Text>
        <Button component={Link} to={normalizedReturn}>
          {t("signin.continue")}
        </Button>
        <Anchor href="/logout">{t("nav.logout")}</Anchor>
      </Stack>
    );
  }

  return (
    <Stack gap="md" maw={480}>
      <Title order={3}>{t("signin.title")}</Title>
      <Text>{t("signin.intro")}</Text>
      <Button onClick={onGoogleSignIn}>{t("signin.withGoogle")}</Button>

      <Divider label={t("signin.orEmail")} labelPosition="center" />

      <form onSubmit={onEmailSignIn}>
        <Stack gap="sm">
          <TextInput
            label={t("signin.email")}
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <PasswordInput
            label={t("signin.password")}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
          />
          {error ? (
            <Text c="red" size="sm">
              {error}
            </Text>
          ) : null}
          <Button type="submit" loading={submitting}>
            {t("signin.withEmail")}
          </Button>
        </Stack>
      </form>

      <Text size="sm">
        <Anchor component={Link} to={`/register?return=${encodeURIComponent(normalizedReturn)}`}>
          {t("signin.createAccount")}
        </Anchor>
        {" · "}
        <Anchor component={Link} to="/forgot-password">
          {t("signin.forgotPassword")}
        </Anchor>
      </Text>

      <Text size="sm" c="dimmed">
        {t("signin.agreePrefix")}{" "}
        <Anchor component={Link} to="/privacy">
          {t("signin.privacyLink")}
        </Anchor>
      </Text>
      {metadata.SignInUrl ? null : (
        <TextInput label={t("signin.debugSignInUrl")} value="(missing)" readOnly />
      )}
    </Stack>
  );
}
